import React, { useEffect, useState, useContext } from 'react';
import { ArrowRightIcon, BellIcon, PencilRulerIcon, DollarSignIcon, ClockIcon, CheckCircleIcon, AlertCircleIcon, TrendingUpIcon, FileTextIcon, CalendarIcon, LayoutDashboardIcon, BarChart3Icon, UsersIcon, ZapIcon, SearchIcon, ShieldIcon, ChevronRightIcon, ChevronDownIcon, MapPinIcon, CloudIcon, RefreshCwIcon, HeadphonesIcon, MailIcon, MessageSquareIcon, StarIcon } from 'lucide-react';
import { FairContext } from '../contexts/FairContext';
import { FairPromotion } from '../components/FairPromotion';
export function HomePageAlternative({
  onNavigate
}: {
  onNavigate?: any;
}) {
  const {
    activeFair,
    setActiveFair
  } = useContext(FairContext);
  const [showPromotion, setShowPromotion] = useState(true);
  // Add an effect to log when the active fair changes
  useEffect(() => {
    if (activeFair && activeFair.hasQRCode) {
      console.log('Home page detected active fair with QR:', activeFair);
      setShowPromotion(true);
    }
  }, [activeFair]);
  const handleClosePromotion = () => {
    setShowPromotion(false);
  };
  // Mock data for alerts and pending orders
  const alerts = [{
    id: 1,
    type: 'order',
    title: 'New order received',
    description: 'Order #HX6AA6-3 received from AiscoEurope SAU',
    time: '10 minutes',
    priority: 'high'
  }, {
    id: 2,
    type: 'quote',
    title: 'Quote pending approval',
    description: 'Quote #DKEYFH-1 awaiting approval',
    time: '2 hours',
    priority: 'medium'
  }, {
    id: 3,
    type: 'design',
    title: 'Design shared',
    description: 'Daniel Fernández shared a design with you',
    time: '1 day',
    priority: 'low'
  }, {
    id: 4,
    type: 'quote',
    title: '3 new quotes created',
    description: 'New quotes have been created in the last 24 hours',
    time: '5 hours',
    priority: 'medium'
  }, {
    id: 5,
    type: 'quote',
    title: '4 quotes expiring soon',
    description: 'Quotes that will expire in the next 7 days',
    time: '1 day',
    priority: 'high'
  }];
  const pendingOrders = [{
    id: 'HX6AA6-3',
    customer: 'AiscoEurope SAU',
    product: 'Pivot Machine 8" 5/8 GALV',
    amount: '€80,108.29',
    status: 'pending',
    date: 'Jun 13, 2025'
  }, {
    id: 'DKEYFH-1',
    customer: 'HMB Irrigation',
    product: 'Reeltown 1058 ft wiper',
    amount: '$64,699.26',
    status: 'review',
    date: 'Jul 21, 2025'
  }, {
    id: 'T55NVQ',
    customer: 'HMB Irrigation',
    product: 'Pivot CP600',
    amount: '€72,350.00',
    status: 'approved',
    date: 'Jul 12, 2025'
  }];
  const stats = [{
    title: 'Quotes',
    value: '12',
    change: '+3',
    icon: <FileTextIcon size={20} className="text-white" data-id="element-9084" />,
    color: 'from-blue-600 to-blue-800'
  }, {
    title: 'Designs',
    value: '8',
    change: '+2',
    icon: <PencilRulerIcon size={20} className="text-white" data-id="element-9085" />,
    color: 'from-blue-500 to-indigo-700'
  }, {
    title: 'Orders',
    value: '5',
    change: '+1',
    icon: <CheckCircleIcon size={20} className="text-white" data-id="element-9086" />,
    color: 'from-indigo-600 to-blue-900'
  }, {
    title: 'Clients',
    value: '24',
    change: '+4',
    icon: <UsersIcon size={20} className="text-white" data-id="element-9087" />,
    color: 'from-blue-700 to-indigo-800'
  }];
  return <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950" data-id="element-9088">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-9089">
        <div className="absolute top-[10%] left-[5%] w-64 h-64 bg-blue-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-9090"></div>
        <div className="absolute top-[40%] right-[10%] w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-9091"></div>
        <div className="absolute bottom-[10%] left-[25%] w-80 h-80 bg-teal-600 rounded-full mix-blend-multiply filter blur-[100px] opacity-20" data-id="element-9092"></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1" data-id="element-9093">
        {/* Fair Promotion Banner (if there's an active fair with QR) */}
        {activeFair && activeFair.hasQRCode && showPromotion && <div className="px-6 md:px-12 pt-6" data-id="element-9094">
            <FairPromotion fair={activeFair} onClose={handleClosePromotion} data-id="element-9095" />
          </div>}
        {/* Hero section */}
        <div className="px-6 md:px-12 pt-16 pb-12 max-w-7xl mx-auto" data-id="element-9096">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-9097">
            {/* Left column - Text and buttons */}
            <div className="flex-1 text-left" data-id="element-9098">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-indigo-200 leading-tight mb-6" data-id="element-9099">
                Design and Quote
                <br data-id="element-9100" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-teal-400" data-id="element-9101">
                  PIVOT System
                </span>
              </h1>
              <p className="text-blue-100 text-lg mb-8 max-w-2xl" data-id="element-9102">
                Create precise and efficient irrigation solutions for any
                terrain with our advanced comprehensive management platform
              </p>
              <div className="flex flex-wrap gap-4 mb-8" data-id="element-9103">
                <button onClick={() => onNavigate('design')} className="flex items-center justify-center bg-gradient-to-r from-blue-800/80 to-indigo-900/80 hover:from-blue-800 hover:to-indigo-900 text-white px-6 py-3.5 rounded-xl text-lg font-medium transition-all shadow-lg hover:shadow-xl hover:translate-y-[-2px] border border-blue-700/30" data-id="element-9104">
                  <PencilRulerIcon size={20} className="mr-2" data-id="element-9105" />
                  Design System
                  <ArrowRightIcon size={18} className="ml-2" data-id="element-9106" />
                </button>
                <button onClick={() => onNavigate('quotes')} className="flex items-center justify-center bg-gradient-to-r from-blue-900/80 to-indigo-800/80 hover:from-blue-900 hover:to-indigo-800 text-white px-6 py-3.5 rounded-xl text-lg font-medium transition-all shadow-lg hover:shadow-xl hover:translate-y-[-2px] border border-indigo-700/30" data-id="element-9107">
                  <DollarSignIcon size={20} className="mr-2" data-id="element-9108" />
                  Create Quote
                  <ArrowRightIcon size={18} className="ml-2" data-id="element-9109" />
                </button>
              </div>
              <div className="flex items-center gap-3 text-blue-200 text-sm" data-id="element-9110">
                <div className="flex -space-x-2" data-id="element-9111">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold" data-id="element-9112">
                    JD
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 flex items-center justify-center text-white text-xs font-bold" data-id="element-9113">
                    MR
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold" data-id="element-9114">
                    TS
                  </div>
                </div>
                <span data-id="element-9115">+24 active users right now</span>
              </div>
            </div>
            {/* Right column - Large pivot image */}
            <div className="flex-1 flex justify-center" data-id="element-9116">
              <div className="relative" data-id="element-9117">
                <div className="absolute -top-16 -left-16 -right-16 -bottom-16 bg-gradient-to-br from-blue-500/10 to-teal-500/10 rounded-full blur-3xl" data-id="element-9118"></div>
                <div className="relative" data-id="element-9119">
                  <div className="grid grid-cols-2 gap-1 absolute -inset-4 -z-10" data-id="element-9120">
                    <div className="bg-gradient-to-br from-blue-600/5 to-transparent h-20 w-20 rounded-md" data-id="element-9121"></div>
                    <div className="bg-gradient-to-br from-indigo-600/5 to-transparent h-20 w-20 rounded-md" data-id="element-9122"></div>
                    <div className="bg-gradient-to-br from-teal-600/5 to-transparent h-20 w-20 rounded-md" data-id="element-9123"></div>
                    <div className="bg-gradient-to-br from-purple-600/5 to-transparent h-20 w-20 rounded-md" data-id="element-9124"></div>
                  </div>
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ddVxmMM9BG37mQQDtAXhse/8120_pierce.svg" alt="PIVOT System" className="h-64 md:h-80 w-auto object-contain drop-shadow-[0_0_15px_rgba(59,130,246,0.3)]" data-id="element-9125" />
                </div>
              </div>
            </div>
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16" data-id="element-9126">
            {stats.map((stat, index) => <div key={index} className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all hover:translate-y-[-2px]" data-id="element-9127">
                <div className={`bg-gradient-to-r ${stat.color} p-5`} data-id="element-9128">
                  <div className="flex justify-between items-center" data-id="element-9129">
                    <div data-id="element-9130">
                      <h3 className="text-sm font-medium text-white/80" data-id="element-9131">
                        {stat.title}
                      </h3>
                      <p className="text-3xl font-bold text-white mt-1" data-id="element-9132">
                        {stat.value}
                      </p>
                    </div>
                    <div className="bg-white/20 p-2.5 rounded-xl" data-id="element-9133">
                      {stat.icon}
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-white/80 mt-3" data-id="element-9134">
                    <TrendingUpIcon size={14} className="mr-1" data-id="element-9135" />
                    <span className="font-medium text-white" data-id="element-9136">
                      {stat.change}
                    </span>
                    <span className="ml-1" data-id="element-9137">this week</span>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Quick Pivot Design Section */}
          <div className="mt-16 bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-lg border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden" data-id="element-9138">
            {/* Background decorations */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full filter blur-3xl -z-10 translate-x-1/2 -translate-y-1/2" data-id="element-9139"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-500/10 rounded-full filter blur-3xl -z-10 -translate-x-1/2 translate-y-1/2" data-id="element-9140"></div>

            <div className="flex flex-col lg:flex-row gap-12 items-center" data-id="element-9141">
              {/* Left: Text */}
              <div className="flex-1 text-left" data-id="element-9142">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-sm font-medium mb-6 border border-blue-500/30" data-id="element-9143">
                  <ZapIcon size={14} className="mr-2" data-id="element-9144" />
                  Lightning Fast Design
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight" data-id="element-9145">
                  Design your next pivot in{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-teal-400" data-id="element-9146">
                    seconds
                  </span>
                </h2>
                <div className="space-y-5 text-blue-100/80 text-lg" data-id="element-9147">
                  <p className="flex items-start" data-id="element-9148">
                    <CheckCircleIcon className="w-6 h-6 text-teal-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-9149" />
                    <span data-id="element-9150">
                      Designing a pivot in Aisco Workspace is incredibly easy
                      and intuitive.
                    </span>
                  </p>
                  <p className="flex items-start" data-id="element-9151">
                    <CheckCircleIcon className="w-6 h-6 text-teal-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-9152" />
                    <span data-id="element-9153">
                      Seamlessly choose between our flagship models, including
                      the CP600 and 8120.
                    </span>
                  </p>
                  <p className="flex items-start" data-id="element-9154">
                    <CheckCircleIcon className="w-6 h-6 text-teal-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-9155" />
                    <span data-id="element-9156">
                      Configure the tallest pivot on the market with just a few
                      clicks.
                    </span>
                  </p>
                </div>
              </div>

              {/* Right: Form */}
              <div className="w-full lg:w-[450px] bg-[#0f1d3a]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl relative z-10" data-id="element-9157">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center" data-id="element-9158">
                  <PencilRulerIcon className="w-5 h-5 mr-2 text-blue-400" data-id="element-9159" />
                  Configure Pivot
                </h3>

                <div className="space-y-4" data-id="element-9160">
                  {/* Name */}
                  <div data-id="element-9161">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-9162">
                      Name
                    </label>
                    <input type="text" defaultValue="New Pivot" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-9163" />
                  </div>

                  {/* Length & Area row */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-9164">
                    <div data-id="element-9165">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-9166">
                        Length
                      </label>
                      <div className="relative" data-id="element-9167">
                        <input type="number" defaultValue="50.01" step="0.01" className="w-full bg-white/5 border border-white/10 rounded-xl pl-4 pr-10 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-9168" />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-blue-300/50 text-sm" data-id="element-9169">
                          m
                        </div>
                      </div>
                    </div>
                    <div data-id="element-9170">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-9171">
                        Area
                      </label>
                      <div className="relative" data-id="element-9172">
                        <input type="number" defaultValue="0.786" step="0.001" className="w-full bg-white/5 border border-white/10 rounded-xl pl-4 pr-10 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-9173" />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-blue-300/50 text-sm" data-id="element-9174">
                          ha
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Degrees & Model row */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-9175">
                    <div data-id="element-9176">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-9177">
                        Degrees
                      </label>
                      <div className="relative" data-id="element-9178">
                        <input type="number" defaultValue="360.00" step="0.01" className="w-full bg-white/5 border border-white/10 rounded-xl pl-4 pr-10 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-9179" />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-blue-300/50 text-sm" data-id="element-9180">
                          °
                        </div>
                      </div>
                    </div>
                    <div data-id="element-9181">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-9182">
                        Model
                      </label>
                      <div className="relative" data-id="element-9183">
                        <select className="w-full appearance-none bg-white/5 border border-white/10 rounded-xl pl-4 pr-10 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-9184">
                          <option value="CP600" className="bg-slate-800" data-id="element-9185">
                            CP600
                          </option>
                          <option value="8120" className="bg-slate-800" data-id="element-9186">
                            8120
                          </option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-blue-300/50" data-id="element-9187">
                          <ChevronDownIcon size={16} data-id="element-9188" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4 mt-2 border-t border-white/10" data-id="element-9189">
                    <button onClick={() => onNavigate('design')} className="w-full flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-medium transition-all shadow-lg shadow-blue-900/20" data-id="element-9190">
                      <CheckCircleIcon size={18} className="mr-2" data-id="element-9191" />
                      Create Pivot
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Aisco Library & School Section */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9192">
            {/* Aisco Library Card */}
            <div className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg border border-white/10 rounded-2xl shadow-xl overflow-hidden hover:border-blue-500/30 transition-all" data-id="element-9193">
              <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/5 rounded-full filter blur-3xl -z-10 translate-x-1/3 -translate-y-1/3" data-id="element-9194"></div>
              <div className="flex items-stretch" data-id="element-9195">
                {/* Left: Icon */}
                <div className="flex items-center justify-center px-6 py-8 flex-shrink-0" data-id="element-9196">
                  <img src="https://cdn.magicpatterns.com/uploads/tZ7NrQh25dhLzDu9ipDBZN/Icon-Aisco-Library.svg" alt="" className="h-24 w-auto filter brightness-0 invert opacity-20" data-id="element-9197" />
                </div>
                {/* Right: Content */}
                <div className="flex-1 py-6 pr-8" data-id="element-9198">
                  <img src="https://cdn.magicpatterns.com/uploads/24jGJCEKwMDt9UpeMLAWch/Logo_Aisco_Library.svg" alt="Aisco Library" className="h-12 w-auto filter brightness-0 invert opacity-80 mb-4" data-id="element-9199" />
                  <p className="text-blue-100/70 text-sm leading-relaxed mb-5" data-id="element-9200">
                    Explore the central hub for all our{' '}
                    <span className="text-white font-semibold" data-id="element-9201">
                      corporate documents
                    </span>
                    . Here you'll find the latest flyers, catalogs, manuals, and
                    technical documents, available for download in multiple
                    languages.
                  </p>
                  <a href="#" className="inline-flex items-center px-5 py-2.5 rounded-xl border border-white/20 text-white text-sm font-medium hover:bg-white/10 transition-all group-hover:border-blue-400/40" data-id="element-9202">
                    Visit Aisco Library
                    <ArrowRightIcon size={16} className="ml-2" data-id="element-9203" />
                  </a>
                </div>
              </div>
            </div>

            {/* Aisco School Card */}
            <div className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg border border-white/10 rounded-2xl shadow-xl overflow-hidden hover:border-teal-500/30 transition-all" data-id="element-9204">
              <div className="absolute top-0 right-0 w-48 h-48 bg-teal-500/5 rounded-full filter blur-3xl -z-10 translate-x-1/3 -translate-y-1/3" data-id="element-9205"></div>
              <div className="flex items-stretch" data-id="element-9206">
                {/* Left: Icon */}
                <div className="flex items-center justify-center px-6 py-8 flex-shrink-0" data-id="element-9207">
                  <img src="https://cdn.magicpatterns.com/uploads/1Uu3MXehpc8ubSxFEbfghA/Icon-Aisco-School.svg" alt="" className="h-24 w-auto filter brightness-0 invert opacity-20" data-id="element-9208" />
                </div>
                {/* Right: Content */}
                <div className="flex-1 py-6 pr-8" data-id="element-9209">
                  <img src="https://cdn.magicpatterns.com/uploads/urNs8uWHp9W9chpxJat8Vo/Logo_Aisco_School.svg" alt="Aisco School" className="h-12 w-auto filter brightness-0 invert opacity-80 mb-4" data-id="element-9210" />
                  <p className="text-blue-100/70 text-sm leading-relaxed mb-5" data-id="element-9211">
                    Access your gateway to technical knowledge on our products.
                    Here you'll find{' '}
                    <span className="text-white font-semibold" data-id="element-9212">
                      expert-led courses
                    </span>
                    . Learn at your own pace, enhance your skills, and download
                    materials upon completion.
                  </p>
                  <a href="#" className="inline-flex items-center px-5 py-2.5 rounded-xl border border-white/20 text-white text-sm font-medium hover:bg-white/10 transition-all group-hover:border-teal-400/40" data-id="element-9213">
                    Visit Aisco School
                    <ArrowRightIcon size={16} className="ml-2" data-id="element-9214" />
                  </a>
                </div>
              </div>
            </div>
            {/* Alerts and pending orders section */}
            <div className="px-6 md:px-12 py-12 max-w-7xl mx-auto" data-id="element-9215">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-9216">
                {/* Alerts section */}
                <div className="rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg border border-white/10" data-id="element-9217">
                  <div className="p-5 border-b border-white/10 flex justify-between items-center" data-id="element-9218">
                    <h2 className="text-xl font-bold text-white flex items-center" data-id="element-9219">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center mr-3 shadow-lg" data-id="element-9220">
                        <BellIcon size={20} className="text-white" data-id="element-9221" />
                      </div>
                      Recent Alerts
                    </h2>
                    <div className="flex items-center space-x-2" data-id="element-9222">
                      <div className="px-2.5 py-1 bg-amber-500/20 text-amber-300 text-xs font-medium rounded-full flex items-center" data-id="element-9223">
                        <AlertCircleIcon size={12} className="mr-1" data-id="element-9224" />4 quotes
                        expiring soon
                      </div>
                      <div className="px-2.5 py-1 bg-blue-500/20 text-blue-300 text-xs font-medium rounded-full flex items-center" data-id="element-9225">
                        <FileTextIcon size={12} className="mr-1" data-id="element-9226" />3 new quotes
                      </div>
                      <button className="text-blue-300 hover:text-white transition-colors text-sm flex items-center bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg" data-id="element-9227">
                        View all
                        <ChevronRightIcon size={16} className="ml-1" data-id="element-9228" />
                      </button>
                    </div>
                  </div>
                  <div data-id="element-9229">
                    {alerts.map(alert => <div key={alert.id} className="p-4 hover:bg-white/5 transition-colors border-b border-white/10 last:border-0" data-id="element-9230">
                        <div className="flex items-start" data-id="element-9231">
                          <div className={`mt-1 p-2 rounded-xl ${alert.priority === 'high' ? 'bg-gradient-to-br from-red-500/20 to-rose-500/20' : alert.priority === 'medium' ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20' : 'bg-gradient-to-br from-blue-500/20 to-indigo-500/20'}`} data-id="element-9232">
                            {alert.type === 'order' ? <FileTextIcon size={18} className={alert.priority === 'high' ? 'text-red-400' : alert.priority === 'medium' ? 'text-amber-400' : 'text-blue-400'} data-id="element-9233" /> : alert.type === 'quote' ? <DollarSignIcon size={18} className={alert.priority === 'high' ? 'text-red-400' : alert.priority === 'medium' ? 'text-amber-400' : 'text-blue-400'} data-id="element-9234" /> : <PencilRulerIcon size={18} className={alert.priority === 'high' ? 'text-red-400' : alert.priority === 'medium' ? 'text-amber-400' : 'text-blue-400'} data-id="element-9235" />}
                          </div>
                          <div className="ml-4 flex-1" data-id="element-9236">
                            <h3 className="text-white font-medium" data-id="element-9237">
                              {alert.title}
                            </h3>
                            <p className="text-blue-200 text-sm mt-1" data-id="element-9238">
                              {alert.description}
                            </p>
                            <div className="flex items-center mt-2 text-xs text-blue-300" data-id="element-9239">
                              <ClockIcon size={12} className="mr-1" data-id="element-9240" />
                              {alert.time} ago
                            </div>
                          </div>
                          <button className="text-blue-400 hover:text-white p-2 rounded-lg transition-colors hover:bg-white/10" data-id="element-9241">
                            <ArrowRightIcon size={16} data-id="element-9242" />
                          </button>
                        </div>
                      </div>)}
                  </div>
                  <div className="p-4 bg-gradient-to-r from-white/5 to-white/10" data-id="element-9243">
                    <button className="w-full flex items-center justify-center bg-gradient-to-r from-blue-600/50 to-indigo-600/50 hover:from-blue-600/70 hover:to-indigo-600/70 text-white py-2.5 rounded-xl transition-all" data-id="element-9244">
                      <RefreshCwIcon size={16} className="mr-2" data-id="element-9245" />
                      Refresh alerts
                    </button>
                  </div>
                </div>
                {/* Pending orders section */}
                <div className="rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg border border-white/10" data-id="element-9246">
                  <div className="p-5 border-b border-white/10 flex justify-between items-center" data-id="element-9247">
                    <h2 className="text-xl font-bold text-white flex items-center" data-id="element-9248">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-600 flex items-center justify-center mr-3 shadow-lg" data-id="element-9249">
                        <ClockIcon size={20} className="text-white" data-id="element-9250" />
                      </div>
                      Pending Orders
                    </h2>
                    <button className="text-blue-300 hover:text-white transition-colors text-sm flex items-center bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg" data-id="element-9251">
                      View all
                      <ChevronRightIcon size={16} className="ml-1" data-id="element-9252" />
                    </button>
                  </div>
                  <div className="p-4" data-id="element-9253">
                    <div className="relative" data-id="element-9254">
                      <input type="text" placeholder="Search orders..." className="w-full bg-white/5 border border-white/10 rounded-xl px-10 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50" data-id="element-9255" />
                      <SearchIcon className="absolute left-3.5 top-3 text-blue-300" size={16} data-id="element-9256" />
                    </div>
                  </div>
                  <div className="px-4" data-id="element-9257">
                    {pendingOrders.map((order, index) => <div key={order.id} className={`p-4 rounded-xl mb-3 ${order.status === 'pending' ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20' : order.status === 'review' ? 'bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20' : 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20'}`} data-id="element-9258">
                        <div className="flex justify-between items-start" data-id="element-9259">
                          <div data-id="element-9260">
                            <div className="flex items-center" data-id="element-9261">
                              <span className="text-white font-medium" data-id="element-9262">
                                {order.customer}
                              </span>
                              <span className="ml-2 px-2 py-0.5 bg-white/10 rounded-full text-xs text-blue-200" data-id="element-9263">
                                {order.id}
                              </span>
                            </div>
                            <p className="text-blue-200 text-sm mt-1" data-id="element-9264">
                              {order.product}
                            </p>
                            <div className="mt-2 flex items-center" data-id="element-9265">
                              <span className="text-lg font-bold text-white" data-id="element-9266">
                                {order.amount}
                              </span>
                              <span className="ml-3 text-xs text-blue-300 flex items-center" data-id="element-9267">
                                <CalendarIcon size={12} className="mr-1" data-id="element-9268" />
                                {order.date}
                              </span>
                            </div>
                          </div>
                          <div data-id="element-9269">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${order.status === 'pending' ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-300' : order.status === 'review' ? 'bg-gradient-to-r from-blue-500/20 to-indigo-500/20 text-blue-300' : 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-300'}`} data-id="element-9270">
                              {order.status === 'pending' ? <>
                                  <AlertCircleIcon size={12} className="mr-1" data-id="element-9271" />
                                  Pending
                                </> : order.status === 'review' ? <>
                                  <FileTextIcon size={12} className="mr-1" data-id="element-9272" />
                                  In review
                                </> : <>
                                  <CheckCircleIcon size={12} className="mr-1" data-id="element-9273" />
                                  Approved
                                </>}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-end" data-id="element-9274">
                          <button className="text-blue-300 hover:text-white transition-colors text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg flex items-center" data-id="element-9275">
                            View details
                            <ArrowRightIcon size={14} className="ml-1" data-id="element-9276" />
                          </button>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
              {/* Quote Score Overview */}
              <div className="mt-12" data-id="element-9277">
                <div className="flex justify-between items-center mb-6" data-id="element-9278">
                  <h2 className="text-xl font-bold text-white flex items-center" data-id="element-9279">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center mr-3 shadow-lg" data-id="element-9280">
                      <StarIcon size={20} className="text-white" data-id="element-9281" />
                    </div>
                    Quote Score Overview
                  </h2>
                  <button className="text-blue-300 hover:text-white transition-colors text-sm flex items-center bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg" onClick={() => onNavigate('quotes')} data-id="element-9282">
                    View all quotes
                    <ChevronRightIcon size={16} className="ml-1" data-id="element-9283" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-9284">
                  <div className="rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-lg border border-green-500/30 p-5" data-id="element-9285">
                    <div className="flex justify-between items-start" data-id="element-9286">
                      <div data-id="element-9287">
                        <h3 className="text-white text-lg font-medium" data-id="element-9288">
                          High Score
                        </h3>
                        <p className="text-green-300 text-sm mt-1" data-id="element-9289">
                          Almost ready to close
                        </p>
                      </div>
                      <div className="bg-green-500/30 p-2 rounded-lg" data-id="element-9290">
                        <StarIcon size={20} className="text-green-300" data-id="element-9291" />
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-9292">
                      <div className="text-3xl font-bold text-white" data-id="element-9293">3</div>
                      <p className="text-green-300 text-sm mt-1" data-id="element-9294">
                        quotes with score 80+
                      </p>
                    </div>
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 backdrop-blur-lg border border-amber-500/30 p-5" data-id="element-9295">
                    <div className="flex justify-between items-start" data-id="element-9296">
                      <div data-id="element-9297">
                        <h3 className="text-white text-lg font-medium" data-id="element-9298">
                          Medium Score
                        </h3>
                        <p className="text-amber-300 text-sm mt-1" data-id="element-9299">
                          Under evaluation
                        </p>
                      </div>
                      <div className="bg-amber-500/30 p-2 rounded-lg" data-id="element-9300">
                        <StarIcon size={20} className="text-amber-300" data-id="element-9301" />
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-9302">
                      <div className="text-3xl font-bold text-white" data-id="element-9303">4</div>
                      <p className="text-amber-300 text-sm mt-1" data-id="element-9304">
                        quotes with score 50-79
                      </p>
                    </div>
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-red-500/20 to-rose-500/20 backdrop-blur-lg border border-red-500/30 p-5" data-id="element-9305">
                    <div className="flex justify-between items-start" data-id="element-9306">
                      <div data-id="element-9307">
                        <h3 className="text-white text-lg font-medium" data-id="element-9308">
                          Low Score
                        </h3>
                        <p className="text-red-300 text-sm mt-1" data-id="element-9309">
                          Weak or uncertain
                        </p>
                      </div>
                      <div className="bg-red-500/30 p-2 rounded-lg" data-id="element-9310">
                        <StarIcon size={20} className="text-red-300" data-id="element-9311" />
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-9312">
                      <div className="text-3xl font-bold text-white" data-id="element-9313">1</div>
                      <p className="text-red-300 text-sm mt-1" data-id="element-9314">
                        quotes with score below 50
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Go to dashboard button */}
              <div className="mt-12 text-center" data-id="element-9315">
                <button onClick={() => onNavigate('dashboard')} className="inline-flex items-center justify-center bg-gradient-to-r from-indigo-600/80 to-purple-600/80 hover:from-indigo-600 hover:to-purple-600 text-white px-8 py-4 rounded-xl transition-all shadow-lg hover:shadow-xl hover:translate-y-[-2px]" data-id="element-9316">
                  <LayoutDashboardIcon size={20} className="mr-2" data-id="element-9317" />
                  Go to Dashboard
                  <ArrowRightIcon size={18} className="ml-2" data-id="element-9318" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Footer rediseñado */}
      <footer className="mt-auto relative z-10 bg-gradient-to-br from-blue-950 to-indigo-950 border-t border-blue-700/20" data-id="element-9319">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:20px_20px]" data-id="element-9320"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" data-id="element-9321"></div>

        <div className="relative max-w-7xl mx-auto pt-16 pb-12 px-6" data-id="element-9322">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10" data-id="element-9323">
            {/* Logo y descripción */}
            <div className="lg:col-span-2" data-id="element-9324">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/j7rsd4nQRUtzsXBFyA8c5B/AISCO_WORKSPACE_MARCA_NEGATIVO.svg" alt="Aisco Workspace" className="h-10 w-auto filter brightness-0 invert mb-6" data-id="element-9325" />
              <p className="text-blue-200 max-w-md mb-6" data-id="element-9326">
                AISCO Workspace is a comprehensive management platform for
                irrigation systems, providing cutting-edge solutions for design,
                quotation, and management of PIVOT systems.
              </p>
              <div className="flex space-x-4" data-id="element-9327">
                <a href="#" className="text-blue-300 hover:text-white transition-colors p-2 bg-white/5 rounded-full" data-id="element-9328">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-9329">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" data-id="element-9330"></path>
                  </svg>
                </a>
                <a href="#" className="text-blue-300 hover:text-white transition-colors p-2 bg-white/5 rounded-full" data-id="element-9331">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-9332">
                    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" data-id="element-9333"></path>
                  </svg>
                </a>
                <a href="#" className="text-blue-300 hover:text-white transition-colors p-2 bg-white/5 rounded-full" data-id="element-9334">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-9335">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" data-id="element-9336"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" data-id="element-9337"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" data-id="element-9338"></line>
                  </svg>
                </a>
                <a href="#" className="text-blue-300 hover:text-white transition-colors p-2 bg-white/5 rounded-full" data-id="element-9339">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-9340">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" data-id="element-9341"></path>
                    <rect x="2" y="9" width="4" height="12" data-id="element-9342"></rect>
                    <circle cx="4" cy="4" r="2" data-id="element-9343"></circle>
                  </svg>
                </a>
              </div>
            </div>

            {/* Enlaces rápidos */}
            <div data-id="element-9344">
              <h3 className="text-white text-lg font-semibold mb-6" data-id="element-9345">
                Quick Links
              </h3>
              <ul className="space-y-3" data-id="element-9346">
                <li data-id="element-9347">
                  <a href="#" className="text-blue-300 hover:text-white transition-colors flex items-center" data-id="element-9348">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" data-id="element-9349"></span>
                    Dashboard
                  </a>
                </li>
                <li data-id="element-9350">
                  <a href="#" className="text-blue-300 hover:text-white transition-colors flex items-center" data-id="element-9351">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" data-id="element-9352"></span>
                    Design System
                  </a>
                </li>
                <li data-id="element-9353">
                  <a href="#" className="text-blue-300 hover:text-white transition-colors flex items-center" data-id="element-9354">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" data-id="element-9355"></span>
                    Quotes
                  </a>
                </li>
                <li data-id="element-9356">
                  <a href="#" className="text-blue-300 hover:text-white transition-colors flex items-center" data-id="element-9357">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" data-id="element-9358"></span>
                    Orders
                  </a>
                </li>
                <li data-id="element-9359">
                  <a href="#" className="text-blue-300 hover:text-white transition-colors flex items-center" data-id="element-9360">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" data-id="element-9361"></span>
                    Dealers
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal y soporte */}
            <div data-id="element-9362">
              <h3 className="text-white text-lg font-semibold mb-6" data-id="element-9363">
                Support & Legal
              </h3>
              <ul className="space-y-3" data-id="element-9364">
                <li data-id="element-9365">
                  <a href="#" className="text-blue-300 hover:text-white transition-colors flex items-center" data-id="element-9366">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" data-id="element-9367"></span>
                    Help Center
                  </a>
                </li>
                <li data-id="element-9368">
                  <a href="#" className="text-blue-300 hover:text-white transition-colors flex items-center" data-id="element-9369">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" data-id="element-9370"></span>
                    Contact Us
                  </a>
                </li>
                <li data-id="element-9371">
                  <a href="#" className="text-blue-300 hover:text-white transition-colors flex items-center" data-id="element-9372">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" data-id="element-9373"></span>
                    Terms of Service
                  </a>
                </li>
                <li data-id="element-9374">
                  <a href="#" className="text-blue-300 hover:text-white transition-colors flex items-center" data-id="element-9375">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" data-id="element-9376"></span>
                    Privacy Policy
                  </a>
                </li>
                <li data-id="element-9377">
                  <a href="#" className="text-blue-300 hover:text-white transition-colors flex items-center" data-id="element-9378">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" data-id="element-9379"></span>
                    Cookie Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>
          {/* Botones de contacto */}
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-9380">
            <div className="flex flex-wrap gap-3" data-id="element-9381">
              <button className="bg-white/5 hover:bg-white/10 text-blue-200 px-4 py-2 rounded-lg transition-colors flex items-center border border-white/10" data-id="element-9382">
                <HeadphonesIcon size={16} className="mr-2" data-id="element-9383" />
                Support
              </button>
              <button className="bg-white/5 hover:bg-white/10 text-blue-200 px-4 py-2 rounded-lg transition-colors flex items-center border border-white/10" data-id="element-9384">
                <MailIcon size={16} className="mr-2" data-id="element-9385" />
                Contact
              </button>
              <button className="bg-white/5 hover:bg-white/10 text-blue-200 px-4 py-2 rounded-lg transition-colors flex items-center border border-white/10" data-id="element-9386">
                <MessageSquareIcon size={16} className="mr-2" data-id="element-9387" />
                Feedback
              </button>
            </div>
            {/* Copyright */}
            <div className="text-blue-300 text-sm" data-id="element-9388">
              © 2025 Aisco Workspace | Version: 2.2.0
            </div>
          </div>

          {/* Decoración */}
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-600/10 rounded-full filter blur-3xl -z-10" data-id="element-9389"></div>
          <div className="absolute top-12 left-12 w-48 h-48 bg-indigo-600/10 rounded-full filter blur-3xl -z-10" data-id="element-9390"></div>
        </div>
      </footer>
    </div>;
}